/**
 * eSalon — Product detail drawer (DEV-4285)
 *
 * A right-hand slide-out opened by any `[data-routine-learn-more="<handle>"]`
 * button in the surrounding section. One panel per product is already in the
 * DOM (see snippets/esalon-product-drawer.liquid); this reveals the matching
 * one, which keeps every product's Liquid data available without a fetch.
 *
 * Closes on the X, on the backdrop, and on Escape.
 */

class ProductDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-drawer-dialog]');
    this.body = this.querySelector('[data-drawer-body]');
    // NOT `this.title` — HTMLElement already defines `title` as a string (the
    // tooltip attribute), so assigning an element to it stringifies through the
    // IDL setter and every later property access fails.
    this.titleEl = this.querySelector('[data-drawer-title]');
    this.panels = Array.from(this.querySelectorAll('[data-drawer-panel]'));
    this.closeButton = this.querySelector('[data-drawer-close]');

    this.recipe = this.querySelector('[data-recipe]');
    this.recipeDialog = this.querySelector('[data-recipe-dialog]');
    this.recipePanels = Array.from(this.querySelectorAll('[data-recipe-panel]'));

    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    if (this.closeButton) this.closeButton.addEventListener('click', () => this.close());

    const backdrop = this.querySelector('[data-drawer-backdrop]');
    if (backdrop) backdrop.addEventListener('click', () => this.close());

    // Scoped to the drawer, not the document: these only ever exist inside it.
    this.querySelectorAll('[data-recipe-open]').forEach((link) => {
      link.addEventListener('click', () => this.openRecipe(link.dataset.recipeOpen, link));
    });

    this.querySelectorAll('[data-recipe-close]').forEach((node) => {
      node.addEventListener('click', () => this.closeRecipe());
    });

    // Delegated from the document, not from an ancestor: the drawer is rendered
    // OUTSIDE `.esalon-component` (that wrapper's `contain: layout` would make
    // it the containing block for a fixed-position child), so it shares no
    // useful ancestor with the buttons that open it. `data-drawer-target` is
    // what re-links the two, and keeps two drawers on one page apart.
    document.addEventListener('click', this.onDocumentClick);
  }

  /**
   * Both listeners live on `document`, so they outlive the element unless they
   * are taken down here. That matters in the theme editor, which re-renders a
   * whole section on every settings change — without this, each edit would
   * leave another drawer's handler behind, all of them answering the same
   * clicks.
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.unlockScroll();
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-routine-learn-more]');
    if (!trigger) return;

    const target = trigger.dataset.drawerTarget;
    if (target && target !== this.id) return;

    this.open(trigger.dataset.routineLearnMore, trigger);
  }

  open(handle, trigger) {
    const panel = this.panels.find((node) => node.dataset.drawerPanel === handle);
    if (!panel) return;

    // Where focus goes back to on close. A drawer opened from a product card
    // must not dump the user at the top of the document when it closes.
    this.opener = trigger || null;

    this.panels.forEach((node) => {
      node.hidden = node !== panel;
    });

    if (this.titleEl) this.titleEl.textContent = panel.dataset.drawerName || '';

    this.hidden = false;
    // Two frames: the element has to be laid out at translateX(100%) before the
    // class that moves it to 0 lands, or there is nothing to transition from.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('is-open'));
    });

    this.lockScroll();
    document.addEventListener('keydown', this.onKeydown);

    if (this.body) this.body.scrollTop = 0;
    if (this.closeButton) this.closeButton.focus();

    this.setCarousels(panel);
  }

  close() {
    if (this.hidden) return;

    // The popup is a layer of the drawer, so it goes with it.
    this.closeRecipe();

    this.classList.remove('is-open');
    this.unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);

    this.setCarousels(null);

    const finish = () => {
      this.hidden = true;
      this.panels.forEach((node) => {
        node.hidden = true;
      });
      if (this.opener) this.opener.focus();
      this.opener = null;
    };

    // Wait out the slide, but never strand the drawer visible if the
    // transition never fires (reduced motion, a backgrounded tab). The
    // matchMedia call is guarded because a throw here would leave an open
    // overlay with the page scroll still locked — unrecoverable for the user,
    // where the same throw on the way in would merely be a dead link.
    let motionOk = true;
    try {
      motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      motionOk = false;
    }
    if (!motionOk) return finish();

    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      this.dialog.removeEventListener('transitionend', once);
      finish();
    };
    this.dialog.addEventListener('transitionend', once);
    setTimeout(once, 400);
  }

  /** Only the open panel's dial runs; every other one is stopped. */
  setCarousels(activePanel) {
    this.querySelectorAll('[data-benefit-carousel]').forEach((carousel) => {
      const active = activePanel && activePanel.contains(carousel);
      if (active && typeof carousel.start === 'function') carousel.start();
      else if (typeof carousel.stop === 'function') carousel.stop();
    });
  }

  /**
   * The ingredients popup, layered over the drawer rather than over the page.
   * Every product's list is already rendered; this reveals one.
   */
  openRecipe(handle, trigger) {
    const panel = this.recipePanels.find((node) => node.dataset.recipePanel === handle);
    if (!this.recipe || !panel) return;

    this.recipeOpener = trigger || null;

    this.recipePanels.forEach((node) => {
      node.hidden = node !== panel;
    });

    this.recipe.hidden = false;

    const close = this.recipe.querySelector('[data-recipe-close]');
    if (close) close.focus();
  }

  closeRecipe() {
    if (!this.recipe || this.recipe.hidden) return;

    this.recipe.hidden = true;
    this.recipePanels.forEach((node) => {
      node.hidden = true;
    });

    if (this.recipeOpener) this.recipeOpener.focus();
    this.recipeOpener = null;
  }

  get recipeOpen() {
    return Boolean(this.recipe) && !this.recipe.hidden;
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Innermost layer first. Escape on the popup must not also dismiss the
      // drawer underneath it.
      if (this.recipeOpen) this.closeRecipe();
      else this.close();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  /** Keeps Tab inside the topmost open layer. */
  trapFocus(event) {
    const scope = this.recipeOpen ? this.recipeDialog : this.dialog;
    if (!scope) return;

    const focusable = Array.from(
      scope.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((node) => node.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Padding replaces the scrollbar's width so the page behind does not jump
   * sideways as it loses its scrollbar.
   */
  lockScroll() {
    // Idempotent. Opening a second product without closing first would otherwise
    // re-capture the page's overflow as the `hidden` this element just set, and
    // unlocking would then RESTORE `hidden` — leaving the page permanently
    // unscrollable behind a closed drawer.
    if (this.scrollLocked) return;
    this.scrollLocked = true;

    const gap = window.innerWidth - document.documentElement.clientWidth;
    this.previousOverflow = document.body.style.overflow;
    this.previousPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  }

  unlockScroll() {
    // Only undo a lock this element actually took — disconnectedCallback calls
    // this unconditionally, and a drawer that never opened must not release
    // someone else's lock (Dawn's cart drawer holds one too).
    if (!this.scrollLocked) return;
    this.scrollLocked = false;

    document.body.style.overflow = this.previousOverflow || '';
    document.body.style.paddingRight = this.previousPadding || '';
  }
}

if (!customElements.get('product-drawer')) {
  customElements.define('product-drawer', ProductDrawer);
}

/**
 * The rotating benefit dial inside a drawer panel.
 *
 * Three benefits sit on an arc: the highlighted one is large and filled in the
 * centre, the other two are small outline icons either side. Every 2s the
 * highlight advances, matching the live widget's `fadeAnimation` states — for
 * highlighted index i, left is i+1 and right is i+2 (both modulo 3), which is
 * what makes the icons appear to travel around the arc.
 *
 * Runs only while its panel is open. The drawer calls start/stop, so a closed
 * drawer is not paying for three timers per product.
 */
class BenefitCarousel extends HTMLElement {
  connectedCallback() {
    this.benefits = Array.from(this.querySelectorAll('[data-benefit]')).map((node) => ({
      title: node.querySelector('[data-benefit-title]')?.textContent?.trim() || '',
      copy: node.querySelector('[data-benefit-copy]')?.textContent?.trim() || '',
      icon: node.dataset.benefitIcon || '',
      outline: node.dataset.benefitIconOutline || '',
    }));

    this.slots = {
      left: this.querySelector('[data-benefit-slot="left"]'),
      feature: this.querySelector('[data-benefit-slot="feature"]'),
      right: this.querySelector('[data-benefit-slot="right"]'),
      title: this.querySelector('[data-benefit-slot="title"]'),
      copy: this.querySelector('[data-benefit-slot="copy"]'),
    };

    this.index = 0;
    if (this.benefits.length) this.paint();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    // Fewer than three and there is no rotation to do — the dial still paints
    // whatever it has, it just stays put.
    if (this.timer || this.benefits.length < 2) return;
    this.timer = setInterval(() => this.advance(), 2000);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  advance() {
    this.index = (this.index + 1) % this.benefits.length;

    // Fade the swap rather than cutting it, the way the live version does.
    this.classList.add('is-fading');
    setTimeout(() => {
      this.paint();
      this.classList.remove('is-fading');
    }, 300);
  }

  paint() {
    const count = this.benefits.length;
    const at = (offset) => this.benefits[(this.index + offset) % count];

    const feature = at(0);
    if (this.slots.feature) this.slots.feature.src = feature.icon;
    if (this.slots.left) this.slots.left.src = at(1).outline;
    if (this.slots.right) this.slots.right.src = at(2).outline;
    if (this.slots.title) this.slots.title.textContent = feature.title;
    if (this.slots.copy) this.slots.copy.textContent = feature.copy;
  }
}

if (!customElements.get('benefit-carousel')) {
  customElements.define('benefit-carousel', BenefitCarousel);
}
